import React, { Component } from 'react'
import '../styles/GlobalStyle.css'
import { firebase, db } from '../firebase/Firebase'
import { flattenDoc } from '.././Util'
import ColType from '.././Types'
import { Button } from 'antd'
import CustomFooter from '../login/CustomFooter'
import MathText, { shouldRenderMath } from './mathtext'

const ImageBox = ({ src, style, children, imgScale = 1.3, imgScaleWidth = 1.0,
    imageScaleHeight = 1.0, objectFit = 'contain', top = '50%', left = '50%', onClick, onTouchEnd }) => {
    let styleObj = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%)`,
        width: `${imgScale * 100}%`,
        height: `${imgScale * 100}%`,
        objectFit: objectFit,
        pointerEvents: 'none',
        zIndex: 0,
    };
    if (top !== '50%') styleObj.top = top;
    if (left !== '50%') styleObj.left = left;
    if (imgScaleWidth !== 1.0) {
        styleObj.width = `${imgScaleWidth * 100}%`;
    }
    if (imageScaleHeight !== 1.0) {
        styleObj.height = `${imageScaleHeight * 100}%`;
    }
    return <div
        style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            ...style,
        }}
        onClick={onClick}
        onTouchEnd={onTouchEnd}
    >
        <img
            src={`/games/space/${src}`}
            alt=''
            draggable={false}
            style={styleObj}
        />
        <div style={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {children}
        </div>
    </div>
}
const PLAYER_COLORS = {
    player1: '#E05A47',
    player2: '#4A90D9',
}
const GOLD_DROP_SHADOW = 'drop-shadow(0 0 4px rgba(255, 200, 0, 1)) drop-shadow(0 0 10px rgba(255, 215, 0, 0.95)) drop-shadow(0 0 20px rgba(255, 215, 0, 0.7)) drop-shadow(0 0 35px rgba(255, 200, 0, 0.5))'
const GREEN_DROP_SHADOW = 'drop-shadow(0 0 3px rgba(46, 125, 50, 1)) drop-shadow(0 0 7px rgba(46, 125, 50, 0.95)) drop-shadow(0 0 15px rgba(46, 125, 50, 0.7)) drop-shadow(0 0 26px rgba(46, 125, 50, 0.5))'
const RED_DROP_SHADOW = 'drop-shadow(0 0 3px rgba(198, 40, 40, 1)) drop-shadow(0 0 7px rgba(198, 40, 40, 0.95)) drop-shadow(0 0 15px rgba(198, 40, 40, 0.7)) drop-shadow(0 0 26px rgba(198, 40, 40, 0.5))'
const WIN_SCORE = 3
const WAYPOINT_COUNT = 4
const shuffle = (arr) => {
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
            ;[a[i], a[j]] = [a[j], a[i]]
    }
    return a
}

const normalizeAnswerText = (text) =>
    String(text).toLowerCase().trim().replace(/\s+/g, ' ').replace(/[.!?,;:]+$/, '')

// Answers starting with a number are keyed by that number,
// so "10", "10 feet", "10 cm", "$10" are all treated as the same.
const answerSimilarityKey = (text) => {
    const norm = normalizeAnswerText(text)
    const numMatch = norm.match(/^[-+]?\$?\s*(\d[\d,]*(?:\.\d+)?|\.\d+)/)
    if (numMatch) {
        const num = parseFloat(numMatch[1].replace(/,/g, ''))
        if (!isNaN(num)) return `num:${num}`
    }
    return `txt:${norm}`
}

const MAX_QUESTIONS = 6

const normalizeGameData = (src) => {
    const all = shuffle([
        ...(src.problem_solving_questions || []),
        ...(src.conceptual_questions || []),
    ])
        .map(q => ({
            question: q.question,
            answer: (q.answers.find(a => a.correct) || {}).answer,
        }))
        .filter(q => q.answer != null)

    const picked = []
    const used = new Set() // indices into `all`

    const fillPass = (keyFn) => {
        const seenKeys = new Set(picked.map(q => keyFn(q.answer)))
        for (let i = 0; i < all.length && picked.length < MAX_QUESTIONS; i++) {
            if (used.has(i)) continue
            const key = keyFn(all[i].answer)
            if (keyFn && seenKeys.has(key)) continue
            seenKeys.add(key)
            picked.push(all[i])
            used.add(i)
        }
    }

    // 1) Avoid similar answers (same leading number / same normalized text)
    fillPass(answerSimilarityKey)
    // 2) If short, allow similar answers but still avoid exact duplicates
    if (picked.length < MAX_QUESTIONS) fillPass(normalizeAnswerText)
    // 3) If still short, fall back to original behavior (take whatever's left)
    if (picked.length < MAX_QUESTIONS) fillPass(() => Symbol())

    return {
        questions: picked,
        description: src.description,
    }
}
class SpaceGame extends Component {
    state = {
        phase: 'intro',
        gameMode: null,
        currentTurn: 'player1',
        player1Score: 0,
        player2Score: 0,
        questions: [],
        answerPool: [],
        currentQuestionIdx: null,
        showFeedback: false,
        feedbackCorrect: null,
        feedbackMessage: null,
        selectedAnswerIdx: null,
        winner: null,
        game_data: normalizeGameData(this.props.gameQuestions),
    }

    renderMath = (text) => shouldRenderMath(this.state.game_data) ? <MathText text={text} /> : text

    componentDidMount() {
        this.initGame('multi');
    }
    componentDidUpdate(prevProps) {
        // If autoPlay flips on while a question is already showing, kick it off
        if (!prevProps.autoPlay && this.props.autoPlay &&
            this.state.phase === 'playing' && !this.state.showFeedback) {
            this.scheduleAutoAnswer()
        }
    }
    componentWillUnmount() {
        if (this.autoAnswerTimeout) clearTimeout(this.autoAnswerTimeout)
    }
    scheduleAutoAnswer = () => {
        if (!this.props.autoPlay) return
        if (this.autoAnswerTimeout) clearTimeout(this.autoAnswerTimeout)
        this.autoAnswerTimeout = setTimeout(() => {
            const { phase, showFeedback, questions, currentQuestionIdx, answerPool } = this.state
            if (phase !== 'playing' || showFeedback) return
            if (currentQuestionIdx == null || currentQuestionIdx >= questions.length) return

            const correctText = questions[currentQuestionIdx].answer
            const correctIdx = answerPool.findIndex(a => a.text === correctText && !a.removed)
            const wrongIndices = answerPool
                .map((_, i) => i)
                .filter(i => !answerPool[i].removed && answerPool[i].text !== correctText)

            let chosenIdx
            // 70% of the time pick the correct answer
            if (Math.random() < 0.7 && correctIdx !== -1) {
                chosenIdx = correctIdx
            } else if (wrongIndices.length > 0) {
                chosenIdx = wrongIndices[Math.floor(Math.random() * wrongIndices.length)]
            } else if (correctIdx !== -1) {
                chosenIdx = correctIdx
            } else {
                return
            }

            this.handleAnswerSelect(answerPool[chosenIdx], chosenIdx)
        }, 1000) // pause so the question is visible before it auto-answers
    }
    initGame = (mode) => {
        const game_data = normalizeGameData(this.props.gameQuestions)
        const shuffled = shuffle(game_data.questions)
            .map((q, i) => ({ ...q, originalIndex: i }))
        const answerPool = shuffle(shuffled.map((q, i) => ({
            text: q.answer,
            id: i,
            removed: false,
        })))
        this.setState({
            game_data,
            phase: 'playing',
            gameMode: mode,
            currentTurn: 'player1',
            player1Score: 0,
            player2Score: 0,
            questions: shuffled,
            answerPool: answerPool,
            currentQuestionIdx: 0,
            showFeedback: false,
            feedbackCorrect: null,
            feedbackMessage: null,
            selectedAnswerIdx: null,
            winner: null,
        }, () => this.scheduleAutoAnswer())
    }
    handleAnswerSelect = (answer, idx) => {
        if (this.state.showFeedback) return
        if (answer.removed) return
        const { questions, currentQuestionIdx, currentTurn, player1Score, player2Score, answerPool, gameMode } = this.state
        const currentQ = questions[currentQuestionIdx]
        const isCorrect = answer.text === currentQ.answer;

        // report the answer up to Games.js for stats
        if (this.props.onAnswered) this.props.onAnswered(!!isCorrect);

        let newP1 = player1Score, newP2 = player2Score
        if (isCorrect) {
            if (currentTurn === 'player1') newP1++
            else newP2++
        }
        let winner = null
        if (newP1 >= WIN_SCORE) winner = 'player1'
        else if (newP2 >= WIN_SCORE) winner = 'player2'
        let newPool = [...answerPool]
        if (isCorrect) {
            newPool = newPool.map((a, i) => i === idx ? { ...a, removed: true } : a)
        }
        this.setState({
            player1Score: newP1,
            player2Score: newP2,
            answerPool: newPool,
            showFeedback: true,
            feedbackCorrect: isCorrect,
            feedbackMessage: isCorrect ? 'Correct!' : 'Not quite!',
            selectedAnswerIdx: idx,
        })
        setTimeout(() => {
            if (winner) {
                setTimeout(() => {
                    this.setState({ phase: 'victory', winner }, () => {
                        if (this.props.autoPlay && this.props.autoPlayOnComplete) {
                            this.props.autoPlayOnComplete()
                        }
                    })
                }, 250)
            } else {
                const nextTurn = gameMode === 'multi'
                    ? (currentTurn === 'player1' ? 'player2' : 'player1')
                    : 'player1'
                // Find remaining questions whose answers are still in the pool
                const remainingQs = questions
                    .map((q, i) => i)
                    .filter(i => {
                        const qAnswer = questions[i].answer
                        return newPool.some(a => a.text === qAnswer && !a.removed)
                    })
                let nextQIdx = currentQuestionIdx
                if (remainingQs.length > 0) {
                    // Find next remaining question after current, wrapping around
                    const after = remainingQs.filter(i => i > currentQuestionIdx)
                    nextQIdx = after.length > 0 ? after[0] : remainingQs[0]
                }
                this.setState({
                    currentTurn: nextTurn,
                    currentQuestionIdx: nextQIdx,
                    showFeedback: false,
                    feedbackCorrect: null,
                    feedbackMessage: null,
                    selectedAnswerIdx: null,
                }, () => this.scheduleAutoAnswer())
            }
        }, 1500)
    }
    resetGame = () => {
        this.setState({
            phase: 'intro', gameMode: null, currentTurn: 'player1',
            player1Score: 0, player2Score: 0, questions: [],
            answerPool: [], currentQuestionIdx: null,
            showFeedback: false, feedbackCorrect: null,
            feedbackMessage: null, selectedAnswerIdx: null, winner: null,
        })
    }
    renderWaypointTrack = (score, side) => {
        const trackTop = side === 'top' ? '7vh' : '16vh'
        const waypoints = []
        const startX = 25
        const endX = 75
        const step = (endX - startX) / (WAYPOINT_COUNT - 1)
        for (let i = 0; i < WAYPOINT_COUNT; i++) {
            const xPos = startX + i * step
            const reached = i <= score
            // Line connecting to next waypoint
            if (i < WAYPOINT_COUNT - 1) {
                const lineWidth = step
                const lineLit = i < score
                waypoints.push(
                    <img
                        key={`${side}-line-${i}`}
                        src='/games/space/line.png'
                        alt=''
                        draggable={false}
                        style={{
                            position: 'absolute',
                            top: trackTop,
                            left: `${xPos + 2}%`,
                            width: `${lineWidth - 2}%`,
                            height: '4vh',
                            objectFit: 'fill',
                            transform: 'translateY(-50%)',
                            opacity: lineLit ? 1 : 0.3,
                            transition: 'opacity 0.5s ease',
                            zIndex: 1,
                        }}
                    />
                )
            }
            // Waypoint
            waypoints.push(
                <img
                    key={`${side}-wp-${i}`}
                    src='/games/space/waypoint-green.png'
                    alt=''
                    draggable={false}
                    style={{
                        position: 'absolute',
                        top: trackTop,
                        left: `${xPos + 1}%`,
                        width: '15vh',
                        height: '15vh',
                        transform: 'translate(-50%, -50%)',
                        opacity: reached ? 1 : 0.3,
                        transition: 'opacity 0.5s ease',
                        zIndex: 2,
                    }}
                />
            )
        }
        // Spaceship
        const shipX = startX + score * step
        const shipSrc = side === 'top' ? 'red-spaceship.png' : 'blue-spaceship.png'
        const isActive = (side === 'top' && this.state.currentTurn === 'player1') ||
            (side === 'bottom' && this.state.currentTurn === 'player2')
        waypoints.push(
            <img
                key={`${side}-ship`}
                src={`/games/space/${shipSrc}`}
                alt=''
                draggable={false}
                style={{
                    position: 'absolute',
                    top: trackTop,
                    left: `${shipX}%`,
                    width: '22vh',
                    height: '22vh',
                    objectFit: 'contain',
                    transform: 'translate(-50%, -50%) scaleX(-1)',
                    transition: 'left 0.6s ease, filter 0.3s ease',
                    zIndex: 3,
                    ...(isActive ? { filter: GOLD_DROP_SHADOW } : {}),
                }}
            />
        )
        return waypoints
    }
    renderIntro = () => {
        return (
            <div style={styles.overlay}>
                <ImageBox src='information-container.png' style={styles.introCard} imgScale={2.2}>
                    <h1 style={styles.title}>Space Race</h1>
                    <p style={styles.description}>
                        {this.state.game_data.description}
                    </p>
                </ImageBox>
                <div style={styles.modeButtons}>
                    <ImageBox
                        src='answer-container.png'
                        style={styles.modeButton}
                        onClick={() => this.initGame('single')}
                        onTouchEnd={(e) => { e.preventDefault(); this.initGame('single') }}
                        imgScale={2.6}
                    >
                        <div style={styles.modeIcon}>🚀</div>
                        <div style={styles.modeLabel}>Single Player</div>
                    </ImageBox>
                    <ImageBox
                        src='answer-container.png'
                        style={styles.modeButton}
                        onClick={() => this.initGame('multi')}
                        onTouchEnd={(e) => { e.preventDefault(); this.initGame('multi') }}
                        imgScale={2.6}
                    >
                        <div style={styles.modeIcon}>👾</div>
                        <div style={styles.modeLabel}>Multiplayer</div>
                    </ImageBox>
                </div>
            </div>
        )
    }
    renderPlaying = () => {
        const { questions, currentQuestionIdx, answerPool, showFeedback, feedbackCorrect,
            feedbackMessage, selectedAnswerIdx, gameMode, currentTurn, player1Score, player2Score } = this.state
        if (currentQuestionIdx == null || currentQuestionIdx >= questions.length) return null
        const currentQ = questions[currentQuestionIdx]
        let turnLabel
        if (gameMode === 'single') {
            turnLabel = <span>Your Turn</span>
        } else if (currentTurn === 'player1') {
            turnLabel = <span><span style={{ color: PLAYER_COLORS.player1 }}>Player 1</span>'s Turn</span>
        } else {
            turnLabel = <span><span style={{ color: PLAYER_COLORS.player2 }}>Player 2</span>'s Turn</span>
        }
        const activeAnswers = answerPool.filter(a => !a.removed)
        return (
            <div style={styles.overlay}>
                {/* Waypoint tracks */}
                {this.renderWaypointTrack(player1Score, 'top')}
                {gameMode === 'multi' && this.renderWaypointTrack(player2Score, 'bottom')}
                {/* Turn badge */}
                <ImageBox src='information-container.png' style={styles.turnBadge} imgScale={2.0}>
                    <span style={styles.turnText}>{turnLabel}</span>
                </ImageBox>
                {/* Question */}
                <ImageBox src='question-container.png' style={styles.questionCard} imgScale={2.0}>
                    <div style={styles.questionText}>
                        {this.renderMath(currentQ.question)}
                    </div>
                </ImageBox>
                {/* Answer pool - 2 rows x 3 cols */}
                <div style={styles.answersGrid}>
                    {answerPool.map((ans, idx) => {
                        if (ans.removed) return (
                            <div key={idx} style={styles.answerCardRemoved} />
                        )
                        let extraStyle = {}
                        if (showFeedback && idx === selectedAnswerIdx) {
                            if (feedbackCorrect) {
                                extraStyle = { transform: 'scale(1.06)', filter: GREEN_DROP_SHADOW }
                            } else {
                                extraStyle = { transform: 'scale(0.94)', filter: RED_DROP_SHADOW }
                            }
                        } else if (showFeedback) {
                            extraStyle = { opacity: 0.5 }
                        }
                        return (
                            <ImageBox
                                key={idx}
                                src='answer-container.png'
                                style={{ ...styles.answerCard, ...extraStyle }}
                                imgScale={2.6}
                                onClick={() => this.handleAnswerSelect(ans, idx)}
                                onTouchEnd={(e) => { e.preventDefault(); this.handleAnswerSelect(ans, idx) }}
                            >
                                <div style={styles.answerText}>
                                    {this.renderMath(ans.text)}
                                </div>
                            </ImageBox>
                        )
                    })}
                </div>
                {/* Remaining answers count */}
                {/*<div style={styles.poolCount}>
                    {activeAnswers.length} answer{activeAnswers.length !== 1 ? 's' : ''} remaining
                </div>*/}
                {/* Feedback banner */}
                {showFeedback && (
                    <ImageBox
                        src='information-container.png'
                        style={{
                            ...styles.feedbackBanner,
                            filter: feedbackCorrect ? GREEN_DROP_SHADOW : RED_DROP_SHADOW,
                        }}
                        imgScale={1.5}
                    >
                        <div style={{
                            ...styles.feedbackText,
                            color: feedbackCorrect ? '#4CAF50' : '#EF5350',
                        }}>
                            {feedbackMessage}
                        </div>
                    </ImageBox>
                )}
            </div>
        )
    }
    renderVictory = () => {
        const { winner, gameMode } = this.state
        let victoryText = gameMode === 'single'
            ? 'You Won!'
            : winner === 'player1' ? 'Player 1 Wins!' : 'Player 2 Wins!'
        const shipSrc = winner === 'player1' ? 'red-spaceship.png' : 'blue-spaceship.png'
        return (
            <div style={styles.overlay}>
                <img
                    src={`/games/space/${shipSrc}`}
                    alt='winner ship'
                    draggable={false}
                    style={{
                        width: '32vh',
                        height: '32vh',
                        objectFit: 'contain',
                        transform: 'scaleX(-1)',
                        filter: GOLD_DROP_SHADOW,
                    }}
                />
                <ImageBox src='information-container.png' style={styles.victoryCard} imgScale={2.2}>
                    <div style={styles.victoryEmoji}>🏆</div>
                    <h1 style={{
                        ...styles.victoryTitle,
                        color: PLAYER_COLORS[winner],
                    }}>{victoryText}</h1>
                    <p style={styles.victorySubtext}>
                        {gameMode === 'single'
                            ? 'You reached the final waypoint!'
                            : `${winner === 'player1' ? 'Player 1' : 'Player 2'} reached the final waypoint first!`}
                    </p>
                </ImageBox>
                <div style={{ marginTop: '2vh' }}>
                    <ImageBox
                        src='answer-container.png'
                        style={styles.playAgainButton}
                        onClick={(e) => { this.initGame('multi') }}
                        onTouchEnd={(e) => { e.preventDefault(); this.initGame('multi') }}
                        imgScale={2.4}
                    >
                        <div style={styles.playAgainText}>Play Again</div>
                    </ImageBox>
                </div>
            </div>
        )
    }
    render() {
        const { phase } = this.state
        return (
            <div style={styles.gameContainer}>
                <img
                    src='/games/space/background.png'
                    alt='background'
                    style={styles.backgroundImage}
                />
                {phase === 'intro' && this.renderIntro()}
                {phase === 'playing' && this.renderPlaying()}
                {phase === 'victory' && this.renderVictory()}
            </div>
        )
    }
}
const styles = {
    gameContainer: {
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        color: '#fff',
    },
    backgroundImage: {
        position: 'fixed',
        top: 0, left: 0, zIndex: 0,
        width: '100vw', height: '100vh',
        objectFit: 'cover',
    },
    overlay: {
        position: 'relative',
        zIndex: 1,
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    // ── Intro ──
    introCard: {
        width: '44vh',
        height: '32vh',
        marginBottom: '5vh',
        padding: '2vh',
    },
    title: {
        fontSize: '4vh',
        fontWeight: 'bold',
        margin: '0 0 1.5vh 0',
        color: '#fff',
        marginTop: '-4vh',
    },
    description: {
        fontSize: '1.8vh',
        lineHeight: 1.6,
        color: '#ddd',
        margin: 0,
        padding: '0 1vh',
        textAlign: 'center',
    },
    modeButtons: {
        display: 'flex',
        gap: '8vh',
        justifyContent: 'center',
    },
    modeButton: {
        width: '20vh',
        height: '20vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
    },
    modeIcon: {
        fontSize: '5vh',
        marginBottom: '0.8vh',
        marginTop: '-2vh',
    },
    modeLabel: {
        fontSize: '2vh',
        fontWeight: '600',
        color: '#fff',
    },
    // ── Turn badge ──
    turnBadge: {
        position: 'absolute',
        top: '22vh',
        width: '22vh',
        height: '10vh',
        textAlign: 'center',
        zIndex: 10,
    },
    turnText: {
        fontWeight: '700',
        fontSize: '2vh',
        textTransform: 'uppercase',
        letterSpacing: '0.1vh',
        color: '#fff',
    },
    // ── Question ──
    questionCard: {
        width: '64vh',
        height: '26vh',
        marginTop: '22vh',
        marginBottom: '3vh',
    },
    questionText: {
        fontSize: '2.6vh',
        fontWeight: '600',
        color: '#fff',
        lineHeight: 1.5,
        textAlign: 'center',
        padding: '0 2vh',
    },
    // ── Answers - 2 rows x 3 cols ──
    answersGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 24vh)',
        gridTemplateRows: 'repeat(2, 16vh)',
        gap: '5vh',
        justifyContent: 'center',
    },
    answerCard: {
        width: '20vh',
        height: '20vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        transition: 'transform 0.2s ease, filter 0.2s ease, opacity 0.4s ease',
    },
    answerCardRemoved: {
        width: '20vh',
        height: '20vh',
        opacity: 0,
        pointerEvents: 'none',
    },
    answerText: {
        fontSize: '1.8vh',
        color: '#fff',
        fontWeight: '600',
        lineHeight: 1.4,
        textAlign: 'center',
        padding: '0 1vh',
    },
    // ── Pool count ──
    poolCount: {
        marginTop: '1.5vh',
        fontSize: '1.6vh',
        color: 'rgba(255,255,255,0.7)',
        fontWeight: '500',
        letterSpacing: '0.05vh',
    },
    // ── Feedback banner ──
    feedbackBanner: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '24vh',
        height: '18vh',
        zIndex: 20,
    },
    feedbackText: {
        fontSize: '2.8vh',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    // ── Victory ──
    victoryCard: {
        width: '44vh',
        height: '28vh',
        padding: '3vh',
        paddingTop: '0vh',
        marginBottom: '3vh',
    },
    victoryEmoji: {
        fontSize: '7vh',
        marginBottom: '1vh',
        marginTop: '-2vh',
    },
    victoryTitle: {
        fontSize: '4vh',
        fontWeight: 'bold',
        margin: '0 0 1vh 0',
    },
    victorySubtext: {
        fontSize: '2vh',
        color: '#ccc',
        margin: '0',
    },
    playAgainButton: {
        width: '23vh',
        height: '23vh',
        cursor: 'pointer',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
    },
    playAgainText: {
        fontSize: '2.2vh',
        fontWeight: 'bold',
        color: '#fff',
    },
}
export default SpaceGame